import React, { useEffect, useState } from "react";
import { Drawer, Table, Tag, Alert, Empty, Spin, Input, Button, Space, Typography, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useMaskCloseGuard from "../useMaskCloseGuard";
import { reasonLabel, maskedPhone, channelText } from "./guestLookup";

const FIELDS = [
  { key: "name", label: "姓名", width: 140 },
  { key: "phone", label: "手機", width: 150 },
  { key: "room", label: "房號", width: 90 },
  { key: "date", label: "入住日", width: 160, type: "date" },
];
const EMPTY_QUERY = { name: "", phone: "", room: "", date: "" };

const money = (v) => (v ? `NT$${Math.round(v).toLocaleString()}` : "—");

// 欄寬一律寫死、內容不斷行：候選人要一眼對齊比較「哪一位才是」，列高參差就比不出來。
// 寬度含排序鈕以外的留白；放不下就水平捲動，不壓縮欄寬。
const CANDIDATE_COLUMNS = [
  { title: "姓名", dataIndex: "name", width: 150, ellipsis: true },
  { title: "手機", dataIndex: "phone_tail", width: 90, render: maskedPhone },
  { title: "來訪次數", dataIndex: "visits", width: 90, align: "right" },
  {
    title: "最近入住",
    key: "last",
    width: 190,
    render: (_, r) => (r.last_ci ? `${r.last_ci}・${r.last_room || "—"}` : "—"),
  },
  {
    title: "符合條件",
    dataIndex: "reasons",
    width: 420,
    render: (reasons) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {reasons.map((r) => (
          <Tag key={r} color={r === "stay" ? "blue" : undefined}>{reasonLabel(r)}</Tag>
        ))}
      </span>
    ),
  },
];
const CANDIDATE_WIDTH = CANDIDATE_COLUMNS.reduce((sum, c) => sum + c.width, 0);

const VISIT_COLUMNS = [
  { title: "入住", dataIndex: "ci", width: 110 },
  { title: "退房", dataIndex: "co", width: 110 },
  { title: "晚數", dataIndex: "nights", width: 70, align: "right" },
  // 換房會有兩個以上的房號
  { title: "房號", dataIndex: "rooms", width: 110, render: (rooms) => rooms.join("、") },
  { title: "房型", dataIndex: "room_types", width: 170, ellipsis: true, render: (types) => types.join("、") || "—" },
  { title: "房價方案", dataIndex: "rate_nam", width: 190, ellipsis: true, render: (v) => v || "—" },
  { title: "通路", key: "channel", width: 210, ellipsis: true, render: (_, v) => channelText(v) },
  { title: "金額", dataIndex: "amount", width: 110, align: "right", render: money },
  {
    title: "需求備註",
    dataIndex: "requests",
    width: 260,
    ellipsis: { showTitle: false },
    render: (requests) => {
      const text = requests.join("；");
      return text ? <Tooltip title={text}><span>{text}</span></Tooltip> : "—";
    },
  },
];
const VISIT_WIDTH = VISIT_COLUMNS.reduce((sum, c) => sum + c.width, 0);

/**
 * 從回饋明細點姓名開啟：用這筆問卷的姓名、手機、房號日期一起查德安住客，列出候選人
 * 與各自的歷次住房。
 *
 * 不只用姓名，是因為問卷填答人常常不是登記的住客 (2026-10 實測 35 筆：只用姓名
 * 找得到 16 筆，加上房號日期與手機後 30 筆)。命中原因標在每位候選人旁邊，
 * 由人判斷是哪一位 —— 畫面不替人認定。
 *
 * 問卷解析出的條件預填在上方，寫錯 (房號多打一位數、年份寫成房號…) 時可以改了重查。
 */
export default function GuestLookupDrawer({ open, feedbackId, onClose, lookupGuest, fetchGuestVisits }) {
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [visits, setVisits] = useState({});
  const maskClosable = useMaskCloseGuard(open);

  const loadVisits = (key) => {
    setVisits((prev) => ({ ...prev, [key]: { loading: true } }));
    fetchGuestVisits(key)
      .then((res) => setVisits((prev) => ({ ...prev, [key]: { data: res.data } })))
      .catch(() => setVisits((prev) => ({ ...prev, [key]: { error: true } })));
  };

  const toggle = (key) => {
    if (expanded.includes(key)) {
      setExpanded(expanded.filter((k) => k !== key));
      return;
    }
    setExpanded([...expanded, key]);
    if (!visits[key]) loadVisits(key);
  };

  const search = (params) => {
    setLoading(true);
    setError(null);
    setExpanded([]);
    lookupGuest(params)
      .then((res) => {
        const data = res.data;
        setResult(data);
        setQuery({ ...EMPTY_QUERY, ...Object.fromEntries(Object.entries(data.query || {}).map(([k, v]) => [k, v || ""])) });
        // 只有一位就直接攤開，省一次點擊
        if (data.candidates.length === 1) {
          const key = data.candidates[0].key;
          setExpanded([key]);
          if (!visits[key]) loadVisits(key);
        }
      })
      .catch((err) => {
        setResult(null);
        setError((err && err.response && err.response.data && err.response.data.message) || "查詢住客失敗，請稍後重試");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && feedbackId) {
      setQuery(EMPTY_QUERY);
      setResult(null);
      setVisits({});
      search({ feedback_id: feedbackId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feedbackId]);

  // 清空的欄位也要送 (空字串)：沒送的話後端會沿用問卷解析出的值
  const research = () => search({ feedback_id: feedbackId, ...query });

  const survey = result && result.survey;
  const surveyNote = survey && [
    survey.room_type && `入住房型 ${survey.room_type}`,
    survey.booking_method && `訂房方式 ${survey.booking_method}`,
  ].filter(Boolean).join("・");

  return (
    <Drawer
      // antd 4.19.5 是 visible 不是 open (open 要到 4.23)。寫錯不會報錯，只是永遠不打開。
      visible={open}
      onClose={onClose}
      maskClosable={maskClosable}
      title="住客住房紀錄"
      width="min(1000px, 100vw)"
      destroyOnClose
    >
      <Space wrap align="end" style={{ marginBottom: 8 }}>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label htmlFor={`guest-lookup-${f.key}`} style={{ display: "block", fontSize: 12, color: "#8c8c8c" }}>
              {f.label}
            </label>
            <Input
              id={`guest-lookup-${f.key}`}
              type={f.type || "text"}
              value={query[f.key]}
              onChange={(e) => setQuery({ ...query, [f.key]: e.target.value })}
              onPressEnter={research}
              style={{ width: f.width }}
              allowClear={f.type !== "date"}
            />
          </div>
        ))}
        <Button icon={<SearchOutlined />} onClick={research} loading={loading}>重新查詢</Button>
      </Space>

      {surveyNote && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {`問卷填寫：${surveyNote}`}
        </Typography.Paragraph>
      )}

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {result && result.truncated && (
        <Alert
          type="warning"
          showIcon
          message="符合的住客超過 50 位，只列出前 50 位；補上手機或房號可以縮小範圍。"
          style={{ marginBottom: 12 }}
        />
      )}

      <Spin spinning={loading}>
        {result && !result.candidates.length ? (
          <Empty description="查無符合的住客，可以修改上方條件後重新查詢" />
        ) : (
          result && (
            <Table
              size="small"
              rowKey="key"
              columns={CANDIDATE_COLUMNS}
              dataSource={result.candidates}
              pagination={false}
              scroll={{ x: CANDIDATE_WIDTH }}
              onRow={(r) => ({ onClick: () => toggle(r.key), style: { cursor: "pointer" } })}
              expandable={{
                expandedRowKeys: expanded,
                onExpand: (_, r) => toggle(r.key),
                expandedRowRender: (r) => <VisitsOf state={visits[r.key]} />,
              }}
            />
          )
        )}
      </Spin>
    </Drawer>
  );
}

function VisitsOf({ state }) {
  if (!state || state.loading) return <Spin size="small" />;
  if (state.error) return <Alert type="error" showIcon message="讀取住房紀錄失敗，請稍後重試" />;

  return (
    <Table
      size="small"
      rowKey={(v) => `${v.ci}|${v.rooms.join(",")}`}
      columns={VISIT_COLUMNS}
      dataSource={state.data.visits}
      pagination={false}
      scroll={{ x: VISIT_WIDTH }}
      locale={{ emptyText: "這位住客沒有住宿紀錄" }}
    />
  );
}
